import { useProductOfferStore } from "@expert/sales-tools";
import { Flex, Indicator, Tabs, Text } from "@mantine/core";
import { useTimeout } from "@mantine/hooks";
import { useState } from "react";

import { isOlderThanFiveMinutes } from "@/customer-panel/components/customer-offers/CustomerOffers.helpers";
import { EligibilityStatus, SaleProgress } from "@/sales-tools/home-product/types";
import { CustomerView } from "@/sales-tools/offers-view/CustomerView";
import { useSessionId, useTypedMostRecentTask } from "@/sdk";
import { useFeatureFlag } from "@/shared-utils";

import { OffersView } from "./OffersView";

interface CustomerOffersLoadedProps {
    startedTimestamp: number;
    isChat: boolean;
    isExpertUniversitySimulatedVoiceTask: boolean;
}

export function CustomerOffersLoaded({
    startedTimestamp,
    isChat,
    isExpertUniversitySimulatedVoiceTask,
}: CustomerOffersLoadedProps) {
    const [activeTab, setActiveTab] = useState<string | null>(
        isExpertUniversitySimulatedVoiceTask ? "offers" : "customer",
    );

    // Switch to offers tab immediately if the call is older than five minutes,
    // otherwise wait 1.5 minutes before switching
    const switchToOffersTabTimeout = useTimeout(
        () => setActiveTab("offers"),
        isOlderThanFiveMinutes(startedTimestamp) ? 0 : 90_000,
        {
            autoInvoke: true,
        },
    );

    const { chatTask } = useTypedMostRecentTask();
    const { data: isChatDeviceDetectionEnabled = false } = useFeatureFlag<boolean>(
        "EWP-chat-isDeviceDetectionEnabled",
        {
            partner: chatTask?.partner,
            topic: chatTask?.topic,
            language: chatTask?.language,
        },
        { isEnabled: isChat },
    );

    const handleSelectTab = (value: string | null) => {
        // If user manually switches tabs, cancel auto-switch
        switchToOffersTabTimeout.clear();

        setActiveTab(value);
    };
    const sessionId = useSessionId();

    const showOffersIndicator = useProductOfferStore
        .getState()
        .getProducts(sessionId)
        .some((p) => p.status === EligibilityStatus.Eligible && p.saleProgress === SaleProgress.InProgress);

    return (
        <Tabs value={activeTab} data-testid="customer-offers-tabs" onChange={handleSelectTab}>
            <Tabs.List grow>
                <Tabs.Tab value="customer" fw={activeTab === "customer" ? 700 : 400}>
                    Customer
                </Tabs.Tab>

                <Tabs.Tab value="offers">
                    <Flex align="center" justify="center">
                        <Text size={"sm"} fw={activeTab === "offers" ? 700 : 400}>
                            Offers
                        </Text>
                        {showOffersIndicator && <Indicator size={6} processing color="primary.5" ml={4} mb={"md"} />}
                    </Flex>
                </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="customer" data-testid="customer-tab-panel">
                <CustomerView hideCustomerInsights={isChat && !isChatDeviceDetectionEnabled} />
            </Tabs.Panel>

            <Tabs.Panel value="offers" data-testid="offers-tab-panel">
                <OffersView isChat={isChat} />
            </Tabs.Panel>
        </Tabs>
    );
}
